import { GameState } from '../objects/GameState';
import { PhaserModalConfig, PhaserModal, ModalOption } from 'src/games/shared/Modal';
import { PhaserButton, PhaserButtonConfig } from 'src/games/shared/Button';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import { Helpers } from '../objects/Helpers';
import initializeInteractiveCursor from 'src/games/shared/InteractiveCursorUtil';
import { Color } from 'src/games/shared/ColorValue';
import TranslateApi from 'src/utils/TranslateHelper';

export class DiagnosisScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    gameState: GameState;
    translateString: string = "Diagnosis.Phaser.DiagnosisScene.";

    constructor() {
        super({
            key: "DiagnosisScene_" + Math.random(),
        });
    }

    preload(): void {
        // All assets were loaded in MainScene
        this.load.tilemapTiledJSON("diagnosis_map", require("/src/assets/qci/maps/labDiagnosisGame/lab_diagnosis_diagnosis_scene.json"));
    }

    create(): void {
        this.initialize();
        this.generateScene();
        initializeInteractiveCursor(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'diagnosis_map' });

        if (!GameState.current.hasSeenQuiz) {
            // Randomly insert correct diagnosis in options if this is the first time showing the diagnosis options
            var random_index = Phaser.Math.Between(0, GameState.current.numberOfDiagnosisOptions + 1);
            GameState.current.diagnosisOptions.splice(random_index, 0, GameState.current.correctDiagnosis);
        }
        GameState.current.hasSeenQuiz = true;
    }

    generateScene() {
        this.background = this.add.image(0, 0, "background");
        this.background.setOrigin(0);
        this.background.setDisplaySize(this.gameWidth, this.gameHeight);

        var notesBackground = this.add.image(50, 50, "pixel").setOrigin(0).setDisplaySize(this.gameWidth - 200, this.gameHeight - 200).setTint(0x000000).setAlpha(0.7);

        this.addObjects();
        Helpers.addBackButton(this);
    }

    addObjects(): void {
        this.map.getObjectLayer("Scene").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "title") {
                var text = TranslateApi.translate(this.translateString + "title");
                let title = this.add.text(ob.x, ob.y + 10, text);
                title.setOrigin(0);
                title.setFontFamily('Russo One, Roboto Mono, Arial');
                title.setTint(0xffffff);
                title.setFontSize(50);
            }
            else if (ob.name === "alien") {
                var alien = this.add.image(ob.x, ob.y, "assets", "sleb_sick_portrait");
                alien.setOrigin(0);
                alien.setDisplaySize(ob.width, ob.height);
            }
        });
        this.showDiagnosisOptions();
    }

    createButton(x: number, y: number, color: number, text: string): PhaserButton {
        var config = new PhaserButtonConfig();
        config.backgroundColor = color;
        config.smartHeight = true;
        var translatedText = TranslateApi.translate(text);
        var button = new PhaserButton(this, x, y, 150, 50, translatedText, config);
        return button;
    }

    /**
     * Shows the diagnosis options for the user to pick from (including the correct one somewhere random)
     * */
    showDiagnosisOptions(): void {
        var x = 100;
        var y = 200;
        this.addTitles();

        // Display different diagnosis options
        for (var i = 0; i < GameState.current.numberOfDiagnosisOptions + 1; i++) {
            var option = GameState.current.diagnosisOptions[i];
            var group = this.add.group();
            var optionDiseaseName = option.diseaseName;

            this.addColorSample(option.colorChangerColor, group);
            this.addCell(option.healthyCellColor, "temp_normal_cell", group);
            this.addCell(option.sickCellColor, "temp_sick_cell", group);
            this.addBacteriaCount(option.bacteriaColor, option.bacteriaCount, group);
            this.addReaction(option.reactionAsset, group);
            this.addDiseaseButton(optionDiseaseName, group);

            Phaser.Actions.GridAlign(group.getChildren(), {
                x: x,
                y: y,
                cellWidth: 175,
                cellHeight: 150,
                height: 1,
                width: 6,
                position: Phaser.Display.Align.CENTER,
            });

            y += 100;
        }
    }

    addTitles(): void {
        var group = this.add.group();
        var x, y;
        x = 100;
        y = 140;
        var labels = TranslateApi.translate(this.translateString + "tableHeaders").split("\n");
        for (var title of labels) {
            var label = this.add.text(0, 0, title);
            label.setFontFamily('Russo One, Roboto Mono, Arial').setColor("#ffffff").setFontSize(25).setX(50 - label.width / 2);
            group.add(this.add.container(0, 0, label));
        }

        Phaser.Actions.GridAlign(group.getChildren(), {
            x: x,
            y: y,
            cellWidth: 175,
            cellHeight: 150,
            height: 1,
            width: labels.length,
            position: Phaser.Display.Align.CENTER,
        });
    }

    addReaction(reaction: string, group: Phaser.GameObjects.Group): void {
        var reactionLabel = TranslateApi.translate(this.translateString + "reaction1");
        if (reaction === "phaser_flare") {
            reactionLabel = TranslateApi.translate(this.translateString + "reaction2");
        }
        var label = this.add.text(0, 0, reactionLabel);
        label.setFontSize(20).setFontFamily('Russo One, Roboto Mono, Arial').setColor("#ffffff").setX(50 - label.width / 2);

        var reactionExample = this.add.image(30, 30, "assets", reaction);
        reactionExample.setOrigin(0);
        reactionExample.setDisplaySize(30, 30);

        var container = this.add.container(0, 0);
        container.setSize(100, 100);
        container.add(label);
        container.add(reactionExample);
        group.add(container);
    }

    addDiseaseButton(name: string, group: Phaser.GameObjects.Group): void {
        // Button to choose that disease
        let optionButton = this.createButton(0, 100, Color.KUDER_SECONDARY_BUTTON, name);
        optionButton.setDataEnabled();
        optionButton.setData("name", name);
        optionButton.on('buttonClicked', () => {
            if (optionButton.getData("name") === GameState.current.correctDiagnosis.diseaseName) {
                this.showModal(true);
            }
            else {
                this.showModal(false);
            }
        });

        group.add(optionButton);
    }

    addCell(color: number, texture: string, group: Phaser.GameObjects.Group): void {
        var cell = this.add.image(0, 0, "assets", texture);
        cell.setOrigin(0);
        cell.setDisplaySize(80, 80);
        cell.setTint(color);

        var container = this.add.container(0, 0);
        container.setSize(100, 100);
        container.add(cell);
        group.add(container);
    }

    addBacteriaCount(color: number, count: number, group: Phaser.GameObjects.Group): void {
        var label = this.add.text(0, 0, "" + count);
        label.setFontSize(50);
        label.setColor("#ffffff");
        label.setFontFamily('Russo One, Roboto Mono, Arial');

        var cell = this.add.image(10, 10, "assets", "temp_bacteria");
        cell.setOrigin(0);
        cell.setDisplaySize(80, 80);
        cell.setTint(color);

        var container = this.add.container(0, 0);
        container.setSize(100, 100);
        container.add(cell);
        container.add(label);
        group.add(container);
    }

    addColorSample(color: number, group: Phaser.GameObjects.Group): void {
        var graphics = this.add.graphics();
        graphics.lineStyle(3, 0x000000);

        let circle = new Phaser.Geom.Circle(50, 50, 40);
        graphics.fillStyle(color);

        var line = graphics.strokeCircleShape(circle);
        var sample = graphics.fillCircleShape(circle);

        var container = this.add.container(0, 0);
        container.setSize(100, 100);
        container.add(sample);
        container.add(line);
        group.add(container);
    }

    /**
     * Show a modal for the feedback on their diagnosis
     * */
    showModal(correct: boolean): void {
        var text;
        if (!correct) {
            // If they don't get it right, start the game over
            text = TranslateApi.translate(this.translateString + "modalText");
            var modalConfig = new PhaserModalConfig();
            modalConfig.title = text;
            modalConfig.buttonWidth = 80;
            modalConfig.buttonConfig = new PhaserButtonConfig();
            modalConfig.buttonConfig.backgroundColor = Color.KUDER_PRIMARY_BUTTON;
            var modalButtonText = TranslateApi.translate("common.ok");

            var modalOptions = [
                { displayText: modalButtonText, id: 2 },
            ];

            var modal = new PhaserModal(this, 0, 0, modalOptions, modalConfig);
            modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
            modal.on('optionClicked', (op: ModalOption) => {
                this.scene.setActive(false);
                this.scene.remove(this);
                // Go back to first test (scene 2)
                var nextSceneObject = Helpers.SCENE_ORDER[2];
                GameState.current.sceneNumber = 3;
                this.scene.add(nextSceneObject.key, nextSceneObject.scene, true);
            });
        }
        else {
            // Go to game over scene
            var nextSceneObject = Helpers.getNextScene(GameState.current.sceneNumber);
            GameState.current.sceneNumber += 1;
            this.scene.add(nextSceneObject.key, nextSceneObject.scene, true);
        }
    }
}



// WEBPACK FOOTER //
// ./src/games/DiagnosisGame/scenes/DiagnosisScene.ts